// src/redux/userSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { IS_DISTRIBUTOR_ROLE, IS_RESELLER_ROLE } from "@utils/user_roles";

const initialState = {
	id: localStorage.getItem("id") || null,
	last_name: localStorage.getItem("last_name") || "",
	first_name: localStorage.getItem("first_name") || "",
	name: localStorage.getItem("name") || "",
	photo: localStorage.getItem("photo") || "",
	email: localStorage.getItem("email") || "",
	role: localStorage.getItem("role") || "",
	token: localStorage.getItem("token") || null,
	has_notification: localStorage.getItem("has_notification") || "",
	all_has_reads: localStorage.getItem("all_has_reads") || null,
	enable_activation: localStorage.getItem("enable_activation") || "",

	permissions: (() => {
		const permissions = localStorage.getItem("permissions");
		try {
			return permissions ? JSON.parse(permissions) : [];
		} catch (e) {
			console.error("Failed to parse permissions from localStorage", e);
			return [];
		}
	})(),
	account_numbers: (() => {
		const account_numbers = localStorage.getItem("account_numbers");
		try {
			return account_numbers ? JSON.parse(account_numbers) : [];
		} catch (e) {
			console.error("Failed to parse account_numbers from localStorage", e);
			return [];
		}
	})(),
	clients: (() => {
		const clients = localStorage.getItem("clients");
		try {
			return clients ? JSON.parse(clients) : [];
		} catch (e) {
			console.error("Failed to parse clients from localStorage", e);
			return [];
		}
	})(),
	distributor: (() => {
		const distributor = localStorage.getItem("distributor");
		try {
			return distributor ? JSON.parse(distributor) : null;
		} catch (e) {
			console.error("Failed to parse distributor from localStorage", e);
			return null;
		}
	})(),

	business_units: (() => {
		const business_units = localStorage.getItem("business_units");
		try {
			return business_units ? JSON.parse(business_units) : [];
		} catch (e) {
			console.error("Failed to parse business_units from localStorage", e);
			return [];
		}
	})(),
};

const userSlice = createSlice({
	name: "auth/user",
	initialState,
	reducers: {
		setUser: (state, action) => {
			state.id = action.payload.id;
			state.last_name =
				typeof action.payload.last_name === "string" ? action.payload.last_name : "";
			state.first_name =
				typeof action.payload.first_name === "string" ? action.payload.first_name : "";
			state.name = action.payload.name;
			state.photo = action.payload.photo;
			state.email = action.payload.email;
			state.role = action.payload.role;
			state.token = action.payload.token;
			state.permissions = action.payload.permissions || [];
			state.account_numbers = action.payload.account_numbers || [];
			state.clients = action.payload.clients || [];
			state.has_intelsat = action.payload.has_intelsat || false;
			state.has_notification = action.payload.has_notification;
			state.all_has_reads = action.payload.all_has_reads;
			state.enable_activation = action.payload.enable_activation;
			state.business_units = action.payload.business_units || [];

			if (
				action.payload.role?.id == IS_DISTRIBUTOR_ROLE ||
				action.payload.role?.parent_id == IS_DISTRIBUTOR_ROLE ||
				action.payload.role?.id == IS_RESELLER_ROLE ||
				action.payload.role?.parent_id == IS_RESELLER_ROLE
			) {
				state.distributor = action.payload.distributor;
				localStorage.setItem("distributor", JSON.stringify(action.payload.distributor));
			}

			localStorage.setItem("id", action.payload.id);
			localStorage.setItem("last_name", action.payload.last_name);
			localStorage.setItem("first_name", action.payload.first_name);
			localStorage.setItem("name", action.payload.name);
			localStorage.setItem("photo", action.payload.photo);
			localStorage.setItem("email", action.payload.email);
			localStorage.setItem("role", action.payload.role);
			localStorage.setItem("token", action.payload.token);
			localStorage.setItem("permissions", JSON.stringify(action.payload.permissions)); // Stringify permissions array
			localStorage.setItem("account_numbers", JSON.stringify(action.payload.account_numbers)); // Stringify account_numbers array
			localStorage.setItem("clients", JSON.stringify(action.payload.clients)); // Stringify clients array
			localStorage.setItem("has_intelsat", action.payload.has_intelsat);
			localStorage.setItem("has_notification", action.payload.has_notification);
			localStorage.setItem("all_has_reads", action.payload.all_has_reads);
			localStorage.setItem("enable_activation", action.payload.enable_activation);
			localStorage.setItem("business_units", JSON.stringify(action.payload.business_units)); // Stringify business_units array
		},
		clearUser: (state) => {
			state.id = null;
			state.last_name = "";
			state.first_name = "";
			state.name = "";
			state.photo = "";
			state.email = "";
			state.role = "";
			state.token = null;
			state.permissions = [];
			state.account_numbers = [];
			state.clients = [];
			state.business_units = [];

			if (state.role === "distributor" && state.distributor) {
				state.distributor = null;
				localStorage.removeItem("distributor");
			} else {
				state.distributor = null;
			}

			localStorage.removeItem("id");
			localStorage.removeItem("last_name");
			localStorage.removeItem("first_name");
			localStorage.removeItem("name");
			localStorage.removeItem("photo");
			localStorage.removeItem("email");
			localStorage.removeItem("role");
			localStorage.removeItem("token");
			localStorage.removeItem("permissions");
			localStorage.removeItem("account_numbers");
			localStorage.removeItem("clients");
			localStorage.removeItem("has_intelsat");
			localStorage.removeItem("has_notification");
			localStorage.removeItem("all_has_reads");
			localStorage.removeItem("enable_activation");
			localStorage.removeItem("business_units");
		},
		setProfilePhoto: (state, action) => {
			state.photo = action.payload;
			localStorage.setItem("photo", action.payload);
		},

		setProfileInformations: (state, action) => {
			const {
				first_name,
				last_name,
				name,
				password,
				confirm_password,
				has_notification,
				enable_notification,
			} = action.payload;

			if (first_name) {
				state.first_name = first_name;
				localStorage.setItem("first_name", first_name);
			}

			if (last_name) {
				state.last_name = last_name;
				localStorage.setItem("last_name", last_name);
			}
			if (name) {
				state.name = name;
				localStorage.setItem("name", name);
			}
			state.password = password;
			state.confirm_password = confirm_password;

			if (enable_notification !== undefined) {
				state.has_notification = enable_notification; // keep one unified field
				localStorage.setItem("has_notification", enable_notification);
			}
		},

		setAllHasReads: (state, action) => {
			state.all_has_reads = action.payload;
		},

		setActivation: (state, action) => {
			state.enable_activation = action.payload;
		},

		setBusinessUnits: (state, action) => {
			state.business_units = action.payload;
		},
	},
});

export const {
	setUser,
	clearUser,
	setProfilePhoto,
	setProfileInformations,
	setActivation,
	setBusinessUnits,
} = userSlice.actions;
export default userSlice.reducer;
